import { CopiableLink } from '@/components/atoms/CopiableLink/CopiableLink'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { OG_API } from '@/constants/constants'
import { useSession } from '@/context/SessionProvider'
import { AppMessages, useAppToast } from '@/hooks/useAppToast'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  SimpleGrid,
  Button,
  Flex,
  Text,
  Link,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { BiLogoTelegram, BiLogoTwitter, BiMailSend } from 'react-icons/bi'
import { Game } from 'types/types'
import LandscapeIcon from '@/public/assets/icons/landscape.svg'
import CopyIcon from '@/public/assets/icons/copy.svg'
import { TrimVariant } from '@/helpers/getTrimmedValue'

export const ShareResultModal = ({ onClose, gameId, game }: { onClose: () => void; gameId: string; game: Game }) => {
  const { t } = useTranslation()
  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('common.game-details', { gameId })}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          <ShareResultContent game={game} gameId={gameId} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const gameMap: Partial<Record<Game, string>> = {
  [Game.DOUBLE]: 'double',
  [Game.JACKPOT]: 'jackpot',
  [Game.MINES]: 'mines',
  [Game.LOOTBOX]: 'lootbox',
  [Game.PLINKO]: 'plinko',
}

export const ShareResultContent = ({ gameId, game }: { gameId: string; game: Game }) => {
  const { t } = useTranslation()
  const [fetching, setFetching] = useState(false)
  const { account } = useSession()

  let link = `${window?.location?.origin}/${gameMap[game]}/history/${gameId}`

  if (account?.ethAddress) {
    link += `?r=${account.ethAddress}`
  }

  const { onSuccess, onError } = useAppToast()

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(link)
      onSuccess({ key: AppMessages.TextCopied })
    } catch {
      onError()
    }
  }

  const handleDownload = () => {
    const downloadURL = account?.injectiveAddress
      ? `${OG_API}/${gameMap[game]}/${gameId}?user=${account?.injectiveAddress}&player=${account?.injectiveAddress}`
      : `${OG_API}/${gameMap[game]}/${gameId}`

    setFetching(true)
    fetch(downloadURL)
      .then((response) => response.blob())
      .then((blob) => {
        const blobURL = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobURL
        a.style.display = 'none'
        a.download = `${gameMap[game]}-${gameId}`
        document.body.appendChild(a)
        a.click()
      })
      .catch((_err) => {
        onError()
      })
      .finally(() => setFetching(false))
  }

  return (
    <>
      <Text fontWeight={700} mb={3}>
        {t('common.share-result-on')}
      </Text>
      <Flex mb={5} gap={3}>
        {socialMedia.map((media, index) => (
          <Link
            href={media.link(link, game)}
            target="_blank"
            key={index}
            cursor={'pointer'}
            _hover={{ bg: 'ninja.400' }}
            transition={'.2s'}
            bg={'ninja.500'}
            rounded={'xl'}
            w={14}
            h={14}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            {media.icon()}
          </Link>
        ))}
      </Flex>
      <Text fontWeight={700} mb={3}>
        {t('common.or-copy-link')}
      </Text>
      <StatsRow value={<CopiableLink trimmedLength={48} variant={TrimVariant.POST} value={link} />} />
      <SimpleGrid columns={2} gap={4} mt={5}>
        <Button
          lineHeight={1}
          isLoading={fetching}
          fontSize={{ base: 'sm', sm: 'md' }}
          onClick={() => handleDownload()}
          width={'full'}
          rounded={'2xl'}
          h={'56px'}
        >
          <Text as={'span'} display={{ base: 'none', sm: 'inline' }}>
            <LandscapeIcon />
          </Text>
          <Text as={'span'} ml={{ base: '0', sm: '2' }}>
            {t('common.download-image')}
          </Text>
        </Button>
        <Button
          lineHeight={1}
          onClick={handleCopy}
          fontSize={{ base: 'sm', sm: 'md' }}
          rounded={'2xl'}
          h={'56px'}
          variant={'orange'}
        >
          <Text as={'span'} display={{ base: 'none', sm: 'inline' }}>
            <CopyIcon />
          </Text>
          <Text as={'span'} ml={{ base: '0', sm: '2' }}>
            {t('common.copy-link')}
          </Text>
        </Button>
      </SimpleGrid>
    </>
  )
}

const socialMedia = [
  {
    link: (url: string, game: Game) => `https://t.me/share/url?url=${url}&text=${descriptions[game]}`,
    icon: () => <BiLogoTelegram size={20} />,
  },
  {
    link: (url: string, game: Game) => `http://x.com/share?text=${descriptions[game]}&url=${url}&hashtags=NinjaBlaze`,
    icon: () => <BiLogoTwitter size={20} />,
  },
  {
    link: (url: string, game: Game) =>
      `mailto:%20?subject=Ninja Blaze — play, win %26 earn!&body=${descriptions[game] + ' \n' + url}`,
    icon: () => <BiMailSend size={20} />,
  },
]

const descriptions: Partial<Record<Game, string>> = {
  [Game.JACKPOT]:
    'Ninja Blaze — play, win %26 earn! That was a fantastic game! Find out if you can hit the jackpot just like that! Jump in and play Jackpot.',
  [Game.DOUBLE]:
    'Ninja Blaze — play, win %26 earn! GG! Rewards multiplied in seconds. Join in and try your luck in the next round of Double!',
  [Game.LOOTBOX]: '',
  [Game.MINES]: '',
}
